'use client';

import React from 'react';
import Image from 'next/image';
import Button from '@mui/material/Button';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CreateResume from './CreateResume';
import ServiceSection from '../ServiceSection';
import { useGsapContextSafe } from '@/utils/hooks/useGsapScroll';
import { usePageStayDuration } from '@/utils/hooks/usePageStayDuration';
import { EVALUATION_INTRO_ID } from '@/constants/component-id';
import { RESUME_EVALUATION_GROUP } from '@/constants/site-track';
import { trackClickEvent } from '@/utils/site-track';
import ResumeIntroImage from '@/assets/resume-evaluation/resume-intro.png';
import Bg1Image from '@/assets/resume-evaluation/bg1.png';
import Bg2Image from '@/assets/resume-evaluation/bg2.png';
import Bg3Image from '@/assets/resume-evaluation/bg3.png';

const Intro = ({ isLogin }: { isLogin: boolean }) => {
  const handleScroll = useGsapContextSafe();

  usePageStayDuration();

  return (
    <>
      <div
        className="my-16"
        data-ele-id="3rd_screen"
        data-ele-group={RESUME_EVALUATION_GROUP}
        data-observer-id="3rd_screen"
      >
        <p className="text-4.5xl text-center font-semibold mb-4">
          为什么需要简历诊断？
        </p>
        <p className="text-center text-xl mb-20">
          你的简历，真的让 HR 看见你了吗
        </p>
        <div className="flex justify-between items-center flex-col tablet:flex-row">
          <div className="tablet:max-w-[680px]">
            <p className="text-2xl font-semibold mb-4">
              <span className="px-2 mr-2">❗</span>
              多数简历的问题，不是写得少，而是写得不对
            </p>
            <p className="mb-8">
              一份简历不仅要写清经历，更要精准传达你的价值。很多求职者在内容结构、表达方式、角色定位等方面存在盲区，可能导致简历被忽视，错失面试机会。
            </p>
            <span className="font-semibold text-xl">简历诊断可以帮你：</span>
            <ul className="list-none mt-4 !ml-0">
              <li className="flex items-center mb-4">
                <CheckRoundedIcon className="!text-base text-web-green-600 mr-3" />
                识别关键问题：分析内容是否清晰体现你的核心能力和岗位匹配度。
              </li>
              <li className="flex items-center mb-4">
                <CheckRoundedIcon className="!text-base text-web-green-600 mr-3" />
                明确优化方向：指出具体在哪些模块需要调整，如个人总结、项目描述、技能呈现等。
              </li>
              <li className="flex items-center">
                <CheckRoundedIcon className="!text-base text-web-green-600 mr-3" />
                提升投递成功率：通过结构化优化建议和示例，让你的简历更具竞争力，更快打动HR。
              </li>
            </ul>
          </div>
          <div className="w-full tablet:w-80">
            <Image
              src={ResumeIntroImage}
              alt=""
              style={{
                objectFit: 'contain',
              }}
              priority
            />
          </div>
        </div>
      </div>
      <div
        className="my-16"
        data-ele-id="4th_screen"
        data-ele-group={RESUME_EVALUATION_GROUP}
        data-observer-id="4th_screen"
      >
        <p className="text-4.5xl text-center font-semibold mb-4">
          诊断核心表现力
        </p>
        <p className="text-center text-xl mb-20">
          洞察招聘逻辑，从定位到表达，系统还原简历背后的竞争力
        </p>
        <div className="h-[414px] flex justify-between items-center flex-row text-sm">
          <div
            className="w-[387px] h-full p-10 shadow-3xl rounded-xl bg-no-repeat bg-cover bg-top"
            style={{ backgroundImage: `url(${Bg1Image.src})` }}
          >
            <p className="text-2xl font-semibold mb-4">职业定位</p>
            <p className="text-base mb-10">你的简历传递了什么样的“你”？</p>
            <p className="mb-4">
              诊断以明确目标岗位为前提，聚焦求职方向是否聚焦清晰、经历是否能支撑该角色、是否传递出“我适合这个岗位”的信号。
            </p>
            <p>让你的简历，不再是泛泛而谈的“求职通用信”。</p>
          </div>
          <div className="grow h-full pl-15 flex flex-col justify-between">
            <div
              className="w-full px-10 py-6 shadow-3xl rounded-xl bg-no-repeat bg-[length:230px] bg-right-top"
              style={{ backgroundImage: `url(${Bg2Image.src})` }}
            >
              <p className="text-2xl font-semibold mb-4">简历策略</p>
              <p className="text-base mb-4">你在用对的方式展示能力吗？</p>
              <p>
                简历不只是陈述你做过什么，而是要让人看到你解决了什么问题、创造了什么结果。我们帮你明确如何优化表达，强化核心能力，让价值不再被埋没。
              </p>
            </div>
            <div className="w-full px-10 py-6 shadow-3xl rounded-xl mt-auto">
              <p className="text-2xl font-semibold mb-4">内容结构</p>
              <p className="text-base mb-4">
                你的简历够完整、有力、有记忆点吗？
              </p>
              <p>
                结构杂乱、信息不全、重点不突出，都会让HR快速跳过。我们会从内容模块的完整性、重点的提炼方式和段落承接上入手，帮你提升可读性和印象点。
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="rounded-lg my-16 [background:linear-gradient(91.84deg,rgba(255,255,255,0.97)_22.58%,rgba(255,255,255,0.93)_87.2%),linear-gradient(0deg,#6792f0,#6792f0),#ffffff] border border-web-blue-500/[0.08] shadow-[0px_0px_20px_0px_#00000014]">
        <div
          className="rounded-lg px-14.5 py-11 bg-contain bg-no-repeat flex items-center justify-between"
          style={{ backgroundImage: `url(${Bg3Image.src})` }}
        >
          <div>
            <p className="font-semibold text-primary text-3xl mb-2">
              一键开始修改你的简历内容
            </p>
            <p className="">抓住每一个细节，轻松修改在线简历，提升面试通过率</p>
          </div>
          <Button
            className="w-full h-13.5 max-w-[340px] !font-semibold tablet:w-[220px]"
            variant="contained"
            onClick={(e) => {
              handleScroll({
                scrollId: EVALUATION_INTRO_ID,
                clearSearchParams: true,
                isCenter: true,
              });
              trackClickEvent(e);
            }}
            data-ele-id="btn_立即诊断"
            data-ele-group={RESUME_EVALUATION_GROUP}
          >
            立即诊断
          </Button>
        </div>
      </div>
      <div
        className="my-16 px-24"
        data-ele-id="5th_screen"
        data-ele-group={RESUME_EVALUATION_GROUP}
        data-observer-id="5th_screen"
      >
        <p className="text-center text-4.5xl font-semibold mb-14">
          服务说明Q&A
        </p>
        <ServiceSection
          title="为什么需要填写岗位再上传简历？"
          detail={
            <>
              <span className="font-semibold">
                填写岗位能让诊断更聚焦精准。
              </span>
              我们会结合该岗位的通用要求与当前行业标准，深入分析你的简历在职业定位、内容结构、表达方式等方面的匹配度，识别不够契合目标岗位的内容，并提供有针对性的优化建议，帮助你朝着更清晰的求职方向调整简历表现。
            </>
          }
        />
        <ServiceSection
          title="上传简历后多久能看到报告？"
          detail="通常在 5 分钟内自动生成诊断报告，您可直接在页面查看并使用。"
        />
        <ServiceSection
          title="没有准备好简历，可以创建吗？"
          detail={
            <>
              当然可以！我们提供
              <CreateResume
                isLogin={isLogin}
                title="简历创建"
                titleClassName="text-web-blue-500"
              />
              工具，支持快速编辑和专业模板样式，帮你快速生成适合投递的简历。
            </>
          }
        />
        <ServiceSection
          title="谁在负责诊断简历？是AI还是人工？"
          detail="完整的诊断报告基于 AI 技术，自动提取简历中的关键信息，并结合多位人力资源专家共同制定的评估标准进行综合分析，既确保了诊断的高效性，也兼顾了专业性的严谨与权威。"
        />
        <ServiceSection
          title="我的简历会被储存或公开吗？"
          detail="不会。你的简历仅用于本次诊断分析，我们不会储存或对外展示任何信息，请放心使用。"
        />
      </div>
    </>
  );
};

export default Intro;
